import { useCallback, useEffect, useState } from "react";

import { trackImageEditToolRedo, trackImageEditToolUndo } from "@design-lab/shared/lib/analytics";
import { useRefineHistoryContext } from "@design-lab/widgets/artboard/model/RefineHistoryContext";

type UseRefineKeyboardShortcutsReturn = {
  isSpaceHeld: boolean;
};

export function useRefineKeyboardShortcuts(artworkId?: string): UseRefineKeyboardShortcutsReturn {
  const { canUndo, canRedo, undo, redo } = useRefineHistoryContext();
  const [isSpaceHeld, setIsSpaceHeld] = useState(false);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const isCtrlOrCmd = isMac ? e.metaKey : e.ctrlKey;

      // Check if user is typing in an input field
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable;

      // Handle Space key for panning (only when not typing)
      if (e.code === "Space" && !e.repeat && !isTyping) {
        e.preventDefault();
        setIsSpaceHeld(true);
        document.body.style.cursor = "grab";
        return;
      }

      // Handle Undo: Ctrl/Cmd + Z
      if (isCtrlOrCmd && e.key === "z" && !e.shiftKey) {
        e.preventDefault();
        if (canUndo) {
          if (artworkId) {
            trackImageEditToolUndo(artworkId);
          }
          undo();
        }
        return;
      }

      // Handle Redo: Ctrl/Cmd + Shift + Z
      if (isCtrlOrCmd && e.key === "z" && e.shiftKey) {
        e.preventDefault();
        if (canRedo) {
          if (artworkId) {
            trackImageEditToolRedo(artworkId);
          }
          redo();
        }
      }
    },
    [canUndo, canRedo, undo, redo, artworkId]
  );

  const handleKeyUp = useCallback((e: KeyboardEvent) => {
    if (e.code === "Space") {
      e.preventDefault();
      setIsSpaceHeld(false);
      document.body.style.cursor = "";
    }
  }, []);

  const handleWindowBlur = useCallback(() => {
    setIsSpaceHeld(false);
    document.body.style.cursor = "";
  }, []);

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("blur", handleWindowBlur);
      document.body.style.cursor = "";
    };
  }, [handleKeyDown, handleKeyUp, handleWindowBlur]);

  return { isSpaceHeld };
}
